import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const resolveCover = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CartDrawer = ({ open, onClose }) => {
  const { cart, removeFromCart, getTotalPrice } = useCart();
  const [step, setStep] = useState('cart'); // 'cart' | 'details'
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Prefill from previous checkout attempt if any
      setEmail(localStorage.getItem('buyer_email') || '');
      setFullName(localStorage.getItem('buyer_full_name') || '');
      setStep('cart');
    }
  }, [open]);

  const goToDetails = () => {
    if (cart.length === 0) return toast.error('Your cart is empty');
    setStep('details');
  };

  const submitCheckout = async (e) => {
    e?.preventDefault?.();
    const trimmedEmail = email.trim();
    const trimmedName = fullName.trim();
    if (!EMAIL_RE.test(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (trimmedName.length < 2) {
      toast.error('Please enter your full name');
      return;
    }
    setSubmitting(true);
    try {
      const sessionId = localStorage.getItem('session_id');
      // Silent promo tokens — attached earlier by PromoAttacher on URL visit.
      let promoTokens = [];
      try {
        promoTokens = JSON.parse(localStorage.getItem('promo_tokens') || '[]');
      } catch { promoTokens = []; }
      const { data } = await axios.post(`${API}/checkout`, {
        items: cart,
        session_id: sessionId,
        email: trimmedEmail,
        full_name: trimmedName,
        promo_tokens: promoTokens,
      });
      localStorage.setItem('buyer_email', trimmedEmail);
      localStorage.setItem('buyer_full_name', trimmedName);
      window.location.href = data.redirectUrl;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || 'Failed to process checkout. Please try again.');
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#121214] border-l border-[#27272A] flex flex-col" data-testid="cart-sidebar">
        <div className="p-4 sm:p-6 border-b border-[#27272A] flex items-center justify-between">
          <div className="flex items-center gap-2">
            {step === 'details' && (
              <button
                onClick={() => setStep('cart')}
                className="text-[#A1A1AA] hover:text-white"
                aria-label="Back to cart"
                data-testid="cart-back-btn"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 className="text-lg sm:text-xl font-heading font-medium">
              {step === 'cart' ? 'Your Cart' : 'Checkout Details'}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="close-cart-button">
            <X size={22} />
          </button>
        </div>

        {step === 'cart' ? (
          <>
            <ScrollArea className="flex-1 p-4 sm:p-6">
              {cart.length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-12">Your cart is empty</p>
              ) : (
                <div className="space-y-3">
                  {cart.map((item) => (
                    <div key={item.album_id} className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-[#18181B] rounded-lg" data-testid={`cart-item-${item.album_id}`}>
                      <img src={resolveCover(item.cover_url)} alt={item.album_title} className="w-14 h-14 sm:w-16 sm:h-16 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate text-sm sm:text-base">{item.album_title}</div>
                        <div className="text-[#00F0FF] font-medium text-sm">₱{item.price.toFixed(2)}</div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.album_id)}
                        className="text-[#A1A1AA] hover:text-[#EF4444]"
                        data-testid={`remove-item-${item.album_id}`}
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            {cart.length > 0 && (
              <div className="p-4 sm:p-6 border-t border-[#27272A] space-y-4">
                <div className="flex justify-between text-base sm:text-lg font-medium">
                  <span>Total</span>
                  <span className="text-[#00F0FF]">₱{getTotalPrice().toFixed(2)}</span>
                </div>
                <Button
                  onClick={goToDetails}
                  className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-medium rounded-full h-12"
                  data-testid="checkout-button"
                >
                  Continue to Checkout
                </Button>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={submitCheckout} className="flex-1 flex flex-col">
            <div className="flex-1 p-4 sm:p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-1.5">Full name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Juan Dela Cruz"
                  autoComplete="name"
                  required
                  minLength={2}
                  maxLength={120}
                  className="w-full px-3 py-2.5 bg-[#0F0F11] border border-[#27272A] rounded-md text-white text-sm focus:border-[#00F0FF] focus:outline-none"
                  data-testid="checkout-fullname-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-1.5">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                  className="w-full px-3 py-2.5 bg-[#0F0F11] border border-[#27272A] rounded-md text-white text-sm focus:border-[#00F0FF] focus:outline-none"
                  data-testid="checkout-email-input"
                />
                <p className="text-[10px] text-[#52525B] mt-1.5">
                  Your download links will be tied to this checkout. Keep this email — support uses it to look up your order if anything goes wrong.
                </p>
              </div>

              <div className="rounded-lg border border-[#27272A] bg-[#0F0F11] p-3 flex items-start gap-2 text-xs text-[#A1A1AA]">
                <ShieldCheck size={16} className="text-[#00F0FF] flex-shrink-0 mt-0.5" />
                <p>
                  You'll be redirected to Maya's secure checkout. We never see your card details.
                </p>
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-[#27272A] space-y-3">
              <div className="flex justify-between text-base sm:text-lg font-medium">
                <span>Pay total</span>
                <span className="text-[#00F0FF]">₱{getTotalPrice().toFixed(2)}</span>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-medium rounded-full h-12 disabled:opacity-60"
                data-testid="checkout-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" /> Redirecting to secure checkout…
                  </>
                ) : (
                  'Pay Now'
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
